/**
 * Helpers for turning a file `HttpResponse` into a download the browser actually saves.
 */

/**
 * `filename*=UTF-8''audit-log-export-20260804142207.csv` — the RFC 5987 extended form. The language
 * segment between the quotes is optional in the grammar but legal when present
 * (`filename*=UTF-8'en'report.csv`), so it is matched rather than required to be empty.
 *
 * Both patterns are anchored to a parameter boundary so a look-alike parameter (`xfilename=…`)
 * can't be read as the file name.
 */
const EXTENDED_FILE_NAME = /(?:^|;)\s*filename\*=(?:[\w-]*)'[^']*'([^;]+)/i;
const PLAIN_FILE_NAME = /(?:^|;)\s*filename=\s*"?([^";]+)"?/i;

/** Anything below U+0020 (plus DEL) is a control character; see {@link stripControlChars}. */
const FIRST_PRINTABLE_CODE_POINT = 0x20;
const DELETE_CODE_POINT = 0x7f;

/**
 * Read the file name out of a `Content-Disposition` header, preferring the RFC 5987 `filename*`
 * form (which is percent-encoded and carries a charset) over the plain `filename`.
 *
 * Returns `null` when the header is absent or carries no usable name, so callers can fall back to
 * a name they compose themselves rather than saving something like `download`.
 *
 * The result is narrowed to a bare file name (see {@link sanitizeFileName}) before any caller puts
 * it on an anchor's `download`.
 */
export function fileNameFromContentDisposition(header: string | null): string | null {
  if (!header) {
    return null;
  }

  // The extended form wins when both are present, per RFC 6266.
  const extended = EXTENDED_FILE_NAME.exec(header);
  if (extended) {
    const decoded = safeDecode(extended[1].trim());
    const name = decoded ? sanitizeFileName(decoded) : null;
    if (name) {
      return name;
    }
  }

  const plain = PLAIN_FILE_NAME.exec(header);
  return plain ? sanitizeFileName(plain[1].trim()) : null;
}

/**
 * Save a `Blob` to the user's machine under `fileName`.
 *
 * The object URL is revoked, which for this app matters beyond tidiness: a download can carry PHI,
 * and an un-revoked blob URL keeps that payload alive in the document for as long as the tab is
 * open. It is revoked on the *next* task rather than in this one — Chromium starts the download
 * synchronously on `click()`, but Firefox and WebKit can abort a download whose blob URL is revoked
 * in the same task, which presents as a silent no-op export.
 *
 * The teardown is in a `finally` for the same reason: if the append or the click throws, an early
 * return would leave the PHI-bearing blob URL resolvable for the lifetime of the tab — the exact
 * thing the revoke exists to prevent.
 */
export function triggerBlobDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  try {
    link.href = url;
    link.download = fileName;
    // Anchors have to be in the document to be clickable in Firefox.
    document.body.appendChild(link);
    link.click();
  } finally {
    // `remove()` rather than `removeChild`: it is a no-op on a node that was never appended, so the
    // cleanup can't throw its own error over the original failure.
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url));
  }
}

/**
 * Read a `Blob` as UTF-8 text.
 *
 * Prefers `Blob.text()` and falls back to `FileReader` for environments that don't implement it
 * (jsdom, which the specs run in, is one). Needed because a request made with `responseType: 'blob'`
 * gets its *error* body as a `Blob` too, so a JSON error payload has to be read back out.
 */
export function readBlobAsText(blob: Blob): Promise<string> {
  if (typeof blob.text === 'function') {
    return blob.text();
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('Unable to read the response body.'));
    reader.readAsText(blob);
  });
}

/** Percent-decode a header value, treating a malformed sequence as "no name" rather than throwing. */
function safeDecode(value: string): string | null {
  try {
    return decodeURIComponent(value) || null;
  } catch {
    return null;
  }
}

/**
 * Reduce a header-supplied value to a bare file name: last path segment only, no control
 * characters, no leading dots.
 *
 * Browsers already sanitize `link.download` and this header comes from our own API, so this is
 * future-proofing rather than a live hole — but the value is still attacker-shaped input (it
 * originates in a response header) being written straight into the DOM, and narrowing it here costs
 * nothing. Returns `null` when nothing usable survives, so the caller falls back to a composed name.
 */
function sanitizeFileName(value: string): string | null {
  const lastSegment = value.split(/[\\/]/).at(-1) ?? '';
  return stripControlChars(lastSegment).replace(/^\.+/, '').trim() || null;
}

/**
 * Drop control characters, filtered by code point rather than with a regex literal so the
 * `no-control-regex` lint rule stays satisfied without a suppression.
 */
function stripControlChars(value: string): string {
  return Array.from(value)
    .filter((char) => {
      const code = char.codePointAt(0) ?? 0;
      return code >= FIRST_PRINTABLE_CODE_POINT && code !== DELETE_CODE_POINT;
    })
    .join('');
}
